import { Button } from "@/components/ui/baseUi/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxShopItemEntity } from "@/modules/boxShop/entities/boxShopItemEntity"
import { BoxShopItemCategory } from "@/modules/boxShop/gateway/boxShopApiDto"
import {
  useCreateBoxShopItem,
  useDeleteBoxShopItem,
  useUpdateBoxShopItem,
} from "@/modules/boxShop/hooks/useBoxShopItem"
import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { word } from "@/modules/core/utils/i18n"
import { FinanceFeesAlertCard } from "@/modules/finances/components/financeFeesAlertCard"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { FileUploadImage } from "@/modules/ui/components/fileUpload/fileUploadImage"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { MAX_64_CHARACTERS, MAX_65535_CHARACTERS } from "@/typings/formik"
import emptyShopItemImage from "@images/boxShop/empty-shop-item-image.webp"
import { DEFAULT_MAXIMUM_AMOUNT, formatCurrency } from "@modules/currency/currency"
import { ImagePlusIcon, LoaderCircle, PlusIcon, Trash2Icon } from "lucide-react"
import { useImperativeHandle, useMemo } from "react"
import { z } from "zod"

type BoxShopItemFormProps = {
  item?: BoxShopItemEntity
  className?: string
  ref: React.Ref<BoxShopItemFormRef>
}

export type BoxShopItemFormRef = {
  onClose: () => Promise<boolean>
}

export function BoxShopItemForm({ item, className, ref, ...props }: BoxShopItemFormProps) {
  const { result: currentBox } = useCurrentBox()
  const { hideSidebar } = useSidebar()
  const { createBoxShopItem } = useCreateBoxShopItem()
  const { updateBoxShopItem } = useUpdateBoxShopItem()
  const { deleteBoxShopItem } = useDeleteBoxShopItem()
  const language = useLanguage()
  const confirmPopup = useConfirmPopup()

  const stripeMinimumAmount = currentBox?.stripeMinimumAmount ?? 0
  const stripeMaximumAmount = currentBox?.stripeMaximumAmount ?? DEFAULT_MAXIMUM_AMOUNT
  const stripeAccountCurrency = currentBox?.stripeAccountCurrency

  const categoryOptions = useMemo(() => {
    return Object.values(BoxShopItemCategory).map((category) => {
      return {
        label: word(`boxShop.item.form.category.${category}.label`),
        value: category,
      }
    })
  }, [])

  const validationSchema = z.object({
    image: z.custom<MediaItemFormType>().nullable(),
    title: z
      .string()
      .min(1, word("form.error.title.required"))
      .max(MAX_64_CHARACTERS, word("form.error.title.max", { count: MAX_64_CHARACTERS })),
    brand: z
      .string()
      .min(1, word("form.error.brand.required"))
      .max(MAX_64_CHARACTERS, word("form.error.brand.max", { count: MAX_64_CHARACTERS }))
      .nullable(),
    description: z
      .string()
      .min(1, word("form.error.description.required"))
      .max(MAX_65535_CHARACTERS, word("form.error.description.max", { count: MAX_65535_CHARACTERS })),
    category: z
      .enum(BoxShopItemCategory)
      .nullable()
      .refine((value) => value !== null, { message: word("form.error.category.required") }),
    price: z
      .number({ message: word("form.error.price.required") })
      .min(
        stripeMinimumAmount,
        word("form.error.price.min", {
          amountAndCurrency: formatCurrency(stripeMinimumAmount, stripeAccountCurrency, language),
        }),
      )
      .max(
        stripeMaximumAmount,
        word("form.error.price.max", {
          amountAndCurrency: formatCurrency(stripeMaximumAmount, stripeAccountCurrency, language),
        }),
      ),
    stock: z
      .number()
      .min(0, word("form.error.stock.min", { count: 0 }))
      .max(999, word("form.error.stock.max", { count: 999 }))
      .nullable(),
  })

  const form = useAppForm({
    defaultValues: BoxShopItemEntity.fromEntityToFormik(item),
    validators: {
      onChange: validationSchema,
      onSubmit: validationSchema,
    },
    onSubmit: async ({ value }) => {
      if (item) {
        await updateBoxShopItem({ id: item.id, data: BoxShopItemEntity.fromFormikToInputDto(value) })
        toast.success(word("boxShop.item.toast.update.success"), { classNames: successToastClassName() })
        hideSidebar(false)
      } else {
        await createBoxShopItem(BoxShopItemEntity.fromFormikToInputDto(value))
        toast.success(word("boxShop.item.toast.create.success"), { classNames: successToastClassName() })
        hideSidebar(false)
      }
    },
  })

  useImperativeHandle(ref, () => ({
    onClose: async () => {
      if (form.state.isDirty) {
        return new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: item
              ? word("boxShop.item.form.modale.cancel.edition.title")
              : word("boxShop.item.form.modale.cancel.creation.title"),
            message: word("boxShop.item.form.modale.cancel.message"),
            accept: () => {
              resolve(true)
            },
            cancel: () => {
              resolve(false)
            },
            footerProps: {
              confirmText: word("global.ok"),
            },
          })
        })
      }
      return Promise.resolve(true)
    },
  }))

  function handleCancelOrDelete() {
    if (item) {
      hideSidebar(false)
      confirmPopup.show({
        title: word("boxShop.item.form.modale.delete.title"),
        message: <></>,
        accept: async () => {
          await deleteBoxShopItem(item.id)
            .then(() => {
              toast.success(word("boxShop.item.toast.delete.success"), { classNames: successToastClassName() })
            })
            .catch(() => {
              toast.error(word("boxShop.item.toast.delete.error"), { classNames: errorToastClassName() })
            })
        },
      })
    } else {
      if (!form.state.isDirty) hideSidebar(false)
      else hideSidebar(true)
    }
  }

  return (
    <div className={cn("flex h-full w-full flex-col gap-8 px-6 pt-12 pb-6", className)} {...props}>
      {/* Header */}
      <div className="flex flex-col">
        <Text fontStyle="TL">
          {item ? word("boxShop.item.form.update.title") : word("boxShop.item.form.create.title")}
        </Text>
        <Text fontStyle="BM">{word("boxShop.item.form.description.text")}</Text>
      </div>

      {/* Form - Image */}
      <form.AppField name="image">
        {(field) => {
          const image = field.state.value
          const urlImage = image?.file ? URL.createObjectURL(image.file) : (image?.url ?? undefined)
          return (
            <div className="relative flex h-72 w-50">
              {urlImage ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="iconMd"
                        className="absolute top-2 right-1 z-10"
                        onClick={() => {
                          field.handleChange(null)
                        }}
                      >
                        <Trash2Icon color="currentColor" className="text-textDefault-body" />
                      </Button>
                    }
                  />
                  <TooltipContent side="bottom">{word("global.picture.delete")}</TooltipContent>
                </Tooltip>
              ) : null}
              <form.Subscribe selector={(state) => state.isSubmitting}>
                {(isSubmitting) => (
                  <FileUploadImage
                    setImage={(newImage) => {
                      if (newImage) field.handleChange(newImage)
                    }}
                    disabled={isSubmitting}
                    className="h-72 w-50 hover:opacity-100"
                  >
                    {urlImage ? (
                      <div
                        className="flex h-full w-full rounded-3xl bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: `url('${urlImage}')` }}
                        aria-label="Shop item image"
                        role="img"
                      />
                    ) : (
                      <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-3xl bg-cover bg-center bg-no-repeat">
                        <div className="absolute z-1 h-full w-full bg-black opacity-50 hover:opacity-70"></div>
                        <img
                          src={emptyShopItemImage}
                          alt="Empty shop item image"
                          className="absolute z-0 h-full w-full opacity-60 hover:opacity-40"
                        />
                        <div className="z-2 flex flex-col items-center justify-center data-[loading=true]:opacity-50">
                          <LoaderCircle className="hidden animate-spin group-data-[loading=true]/dropzone:block" />
                          <ImagePlusIcon className="block group-data-[loading=true]/dropzone:hidden" />
                          <Text fontStyle="TS">{word("boxShop.item.form.image.select")}</Text>
                        </div>
                      </div>
                    )}
                  </FileUploadImage>
                )}
              </form.Subscribe>
            </div>
          )
        }}
      </form.AppField>

      {/* Form - Title */}
      <form.AppField name="title">
        {(field) => (
          <field.Field>
            <field.Label required>{word("boxShop.item.form.title.label")}</field.Label>
            <field.Content>
              <field.Input placeholder={word("boxShop.item.form.title.placeholder")} />
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>

      {/* Form - Brand */}
      <form.AppField name="brand">
        {(field) => (
          <field.Field>
            <field.Label>{word("boxShop.item.form.brand.label")}</field.Label>
            <field.Content>
              <field.Input
                value={field.state.value ?? ""}
                onChange={(e) => {
                  field.handleChange(e.target.value === "" ? null : e.target.value)
                }}
                placeholder={word("boxShop.item.form.brand.placeholder")}
              />
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>

      {/* Form - Description */}
      <form.AppField name="description">
        {(field) => (
          <field.Field>
            <field.Label required>{word("boxShop.item.form.description.label")}</field.Label>
            <field.Content>
              <field.Textarea
                className="field-sizing-fixed"
                rows={5}
                placeholder={word("boxShop.item.form.description.placeholder")}
              />
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>

      {/* Form - Category */}
      <form.AppField name="category">
        {(field) => (
          <field.Field>
            <field.Label required>{word("boxShop.item.form.category.label")}</field.Label>
            <field.Content>
              <Select
                defaultValue={field.state.value ?? undefined}
                onValueChange={(value) => {
                  field.handleChange(value ?? null)
                }}
                items={categoryOptions}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder={word("boxShop.item.form.category.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>

      {/* Form - Price & Stock */}
      <div className="flex w-full flex-row gap-2">
        <form.AppField name="price">
          {(field) => (
            <field.Field className="flex-1">
              <field.Label required>{word("boxShop.item.form.price.label")}</field.Label>
              <field.Content>
                <field.PriceInput
                  currency={stripeAccountCurrency ?? undefined}
                  locale={language}
                  inputProps={{ placeholder: word("boxShop.item.form.price.placeholder") }}
                />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <form.AppField name="stock">
          {(field) => (
            <field.Field className="flex-1">
              <field.Label>{word("boxShop.item.form.stock.label")}</field.Label>
              <field.Content>
                <field.NumberInput
                  step={1}
                  min={0}
                  max={999}
                  placeholder={word("boxShop.item.form.stock.placeholder")}
                />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>
      </div>

      <FinanceFeesAlertCard />

      {/* Form - Save & Cancel */}
      <form.Subscribe selector={(state) => [state.isSubmitting, state.canSubmit, state.isDirty] as const}>
        {([isSubmitting, canSubmit, isDirty]) => (
          <div className="flex flex-row gap-8">
            <Button
              size="lg"
              variant="outline"
              className="flex-1"
              disabled={isSubmitting}
              onClick={(event) => {
                handleCancelOrDelete()
                event.preventDefault()
                event.stopPropagation()
              }}
            >
              {item ? (
                <>
                  <Trash2Icon /> {word("global.delete")}
                </>
              ) : (
                word("global.cancel")
              )}
            </Button>
            <Button
              size="lg"
              variant="primary"
              className="flex-1"
              disabled={isSubmitting || !canSubmit || !isDirty}
              onClick={() => {
                void form.handleSubmit()
              }}
              isLoading={isSubmitting}
            >
              {item ? (
                word("global.save")
              ) : (
                <>
                  {isSubmitting ? <LoaderCircle className="animate-spin" /> : <PlusIcon />}
                  {word("boxShop.item.text")}
                </>
              )}
            </Button>
          </div>
        )}
      </form.Subscribe>
    </div>
  )
}
